import type { FixedThemes, ThemeExtensions } from '@frameio/vapor-theme';
import { theme } from '@frameio/vapor-theme';
import type {
  AccentNeutralBrand,
  AccentNeutralColorsOptions,
  CustomAccentNeutralColors,
  CustomBrandGeneratorOptions,
  HexOrRgbColorString,
} from '../types';
import { isColorLightOrDark, shade, tint } from './colorUtils';
import { generateCustomBrand } from './customBranding';

/**
 * Takes an rgb or hex color and returns the theme name of the fixed theme (light or dark) that is closest in lightness.
 * @returns FixedThemes
 */
export const getSimilarLightnessThemeName = ({
  backgroundColor,
}: AccentNeutralColorsOptions): FixedThemes => {
  return isColorLightOrDark<FixedThemes>({
    color: backgroundColor,
    returnIfLightColor: 'light',
    returnIfDarkColor: 'dark',
  });
};

/**
 * Takes an rgb or hex color and determines the name of the accent neutral theme needed based on contrast.
 * @returns AccentNeutralTheme
 */
export const getAccentNeutralThemeName = ({
  backgroundColor,
}: AccentNeutralColorsOptions): ThemeExtensions => {
  const contrast = getSimilarLightnessThemeName({ backgroundColor });
  return `${contrast}-accentNeutral` as ThemeExtensions;
};

/**
 * Takes an rgb or hex color and generates values for the brand tokens that work with the custom color for the accent neutral theme.
 * @returns AccentNeutralBrand
 */
export const generateCustomAccentNeutralBrand = ({
  primaryColor: primaryColorProp,
}: CustomBrandGeneratorOptions): AccentNeutralBrand => {
  const contrastColor = isColorLightOrDark<FixedThemes>({
    color: primaryColorProp,
    returnIfLightColor: 'dark',
    returnIfDarkColor: 'light',
  });
  const primaryColor = (
    contrastColor === 'dark'
      ? shade({ percentage: 70, color: primaryColorProp })
      : tint({ percentage: 70, color: primaryColorProp })
  ) as HexOrRgbColorString;
  return generateCustomBrand({ primaryColor });
};

/**
 * Takes an rgb or hex color and generates the accent neutral className as well as
 * the accent neutral theme needed based on contrast and a tint of the color for Popovers.
 * @returns CustomAccentNeutralColors
 */
export const generateAccentNeutralColors = ({
  backgroundColor,
  textColor,
}: AccentNeutralColorsOptions): CustomAccentNeutralColors => {
  const contrast = isColorLightOrDark<FixedThemes>({
    color: backgroundColor,
    returnIfLightColor: 'light',
    returnIfDarkColor: 'dark',
  });
  return {
    themeName: getAccentNeutralThemeName({ backgroundColor }),
    backgroundPanel: backgroundColor,
    backgroundSecondary: (contrast === 'light'
      ? shade({ percentage: 8, color: backgroundColor })
      : tint({
          percentage: 8,
          color: backgroundColor,
        })) as HexOrRgbColorString,
    backgroundPrimary: (contrast === 'light'
      ? tint({ percentage: 8, color: backgroundColor })
      : shade({
          percentage: 8,
          color: backgroundColor,
        })) as HexOrRgbColorString,
    playerTitleToken: textColor || theme.colors.text.body,
    backgroundActivated: shade({
      percentage: 5,
      color: backgroundColor,
    }) as HexOrRgbColorString,
    backgroundColor,
    textColor,
  };
};
